
import React from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { MessageSquare, Calendar, Coins, Gift } from "lucide-react";
import { Button } from "@/components/ui/button";

const New2HowItWorks = () => {
  const { language } = useLanguage();
  
  const content = {
    fr: {
      title: "Comment ça marche ?",
      subtitle: "4 étapes simples pour créer le cadeau parfait",
      cta: "Commencer maintenant",
      steps: [
        {
          icon: MessageSquare,
          title: "1. Créez votre message",
          description: "Enregistrez un message vidéo, écrivez une lettre, ou ajoutez des photos qui seront révélés au moment choisi."
        },
        {
          icon: Calendar,
          title: "2. Choisissez le moment",
          description: "Définissez quand votre cadeau sera dévoilé : 18e anniversaire, diplôme, mariage, ou toute autre date importante."
        },
        {
          icon: Coins,
          title: "3. Ajoutez votre contribution",
          description: "Investissez un montant qui croîtra de manière éthique et durable jusqu'au jour J."
        },
        {
          icon: Gift,
          title: "4. Partagez l'expérience",
          description: "Invitez famille et amis à participer et créer ensemble une capsule temporelle collective unique."
        }
      ]
    },
    en: {
      title: "How does it work?",
      subtitle: "4 simple steps to create the perfect gift",
      cta: "Start now",
      steps: [
        {
          icon: MessageSquare,
          title: "1. Create your message",
          description: "Record a video message, write a letter, or add photos that will be revealed at the chosen moment."
        },
        {
          icon: Calendar,
          title: "2. Choose the moment",
          description: "Define when your gift will be unveiled: 18th birthday, graduation, wedding, or any other important date."
        },
        {
          icon: Coins,
          title: "3. Add your contribution",
          description: "Invest an amount that will grow ethically and sustainably until the big day."
        },
        {
          icon: Gift,
          title: "4. Share the experience",
          description: "Invite family and friends to participate and create a unique collective time capsule together."
        }
      ]
    },
    de: {
      title: "Wie funktioniert es?",
      subtitle: "4 einfache Schritte, um das perfekte Geschenk zu erstellen",
      cta: "Jetzt starten",
      steps: [
        {
          icon: MessageSquare,
          title: "1. Erstellen Sie Ihre Nachricht",
          description: "Nehmen Sie eine Videonachricht auf, schreiben Sie einen Brief oder fügen Sie Fotos hinzu, die zum gewählten Zeitpunkt enthüllt werden."
        },
        {
          icon: Calendar,
          title: "2. Wählen Sie den Moment",
          description: "Bestimmen Sie, wann Ihr Geschenk enthüllt wird: 18. Geburtstag, Abschluss, Hochzeit oder ein anderes wichtiges Datum."
        },
        {
          icon: Coins,
          title: "3. Fügen Sie Ihren Beitrag hinzu",
          description: "Investieren Sie einen Betrag, der ethisch und nachhaltig bis zum großen Tag wächst."
        },
        {
          icon: Gift,
          title: "4. Teilen Sie die Erfahrung",
          description: "Laden Sie Familie und Freunde ein, teilzunehmen und gemeinsam eine einzigartige kollektive Zeitkapsel zu erstellen."
        }
      ]
    }
  };

  return (
    <section id="how-it-works" className="py-20 md:py-24 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4 md:mb-6">
            {content[language].title}
          </h2>
          <p className="text-lg md:text-xl text-gray-600">
            {content[language].subtitle}
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 mb-8 md:mb-12">
            {content[language].steps.map((step, index) => (
              <div key={index} className="text-center relative h-full">
                {/* Connection line - only on large screens */}
                {index < content[language].steps.length - 1 && (
                  <div className="hidden lg:block absolute top-12 left-1/2 w-full h-0.5 bg-gradient-to-r from-legatia-orange to-legatia-yellow transform translate-x-1/2 z-0"></div>
                )}
                
                <div className="relative z-10 bg-white h-full flex flex-col">
                  <div className="bg-gradient-to-br from-legatia-orange to-legatia-red p-3 md:p-4 rounded-full w-20 h-20 md:w-24 md:h-24 mx-auto mb-4 md:mb-6 flex items-center justify-center flex-shrink-0">
                    <step.icon className="h-8 w-8 md:h-10 md:w-10 text-white" />
                  </div>
                  
                  <div className="flex-1 flex flex-col">
                    <h3 className="text-base md:text-lg font-semibold mb-3 md:mb-4 text-gray-900 min-h-[2.5rem] md:min-h-[3rem] flex items-center justify-center">
                      {step.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed text-sm md:text-base flex-1 flex items-center justify-center">
                      {step.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Button 
              size="lg"
              className="bg-gradient-to-r from-legatia-orange to-legatia-red hover:from-legatia-orange/90 hover:to-legatia-red/90 text-white px-6 md:px-8 py-4 md:py-6 text-base md:text-lg rounded-full"
              onClick={() => window.location.href = "https://app.getlegatia.ch/"}
            >
              {content[language].cta}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default New2HowItWorks;
